import { useEffect, useState } from "react";
import {
  Box, Typography, Button, Link, Divider, TextField, InputAdornment,
  IconButton, Alert, CircularProgress, Checkbox, FormControlLabel,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessIcon from "@mui/icons-material/Business";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isTenantRealm as subdomainDetected } from "../keycloak";
import { defaultAppPath } from "../utils/entitlements";
import logo from "../asserts/logo/Logo.svg";
import icon from "../asserts/logo/Icon.svg";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const KC_URL = process.env.REACT_APP_KEYCLOAK_URL || "https://keycloak.validaite.net";
const KC_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "validaite-frontend";

// SSO — configurable via env vars so this works for any IdP and any realm
const SSO_IDP_HINT = process.env.REACT_APP_SSO_IDP_HINT || "microsoft";
const SSO_ALLOWED_DOMAIN = (process.env.REACT_APP_SSO_ALLOWED_DOMAIN || "").trim();
const SSO_BUTTON_LABEL = process.env.REACT_APP_SSO_LABEL
  || (SSO_IDP_HINT === "microsoft" ? "Sign in with Microsoft" : `Sign in with ${SSO_IDP_HINT}`);
const SSO_HINT_TEXT = SSO_ALLOWED_DOMAIN
  ? `SSO available for @${SSO_ALLOWED_DOMAIN} accounts`
  : "Sign in with your organisation account";

const RESEND_INVITE_SUCCESS =
  "If an invitation is pending, a new invitation email has been sent. Open that email and use the login link.";

/** Shared API call for Resend invitation (login gate + forgot-new-user panel). */
async function requestResendInvitation(identifier, realm) {
  const res = await fetch(`${BACKEND}/api/resend-invitation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      usernameOrEmail: String(identifier).toLowerCase(),
      realm,
    }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, message: data.message || null };
}

function Login() {
  const { authenticated, initialized, user, injectTokens } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [tenantSlug, setTenantSlug] = useState("");
  const [slugError, setSlugError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginInfo, setLoginInfo] = useState("");
  const [loginWarning, setLoginWarning] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tenantConfig, setTenantConfig] = useState(null);

  /** Clear Template A gate (warning + Resend) when the user is no longer in a misuse state. */
  const clearFirstTimeGate = () => {
    setLoginWarning("");
    setResendLoading(false);
  };

  // login | forgotLookup | forgotExisting | forgotNewUser — in-card panel swap (no dialog)
  const [authPanel, setAuthPanel] = useState("login");
  const [forgotIdentifier, setForgotIdentifier] = useState("");
  const [forgotResolvedUsername, setForgotResolvedUsername] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [hideForgotPassword, setHideForgotPassword] = useState(false);

  const platformRealm = process.env.REACT_APP_KEYCLOAK_REALM || "validaite";
  const currentRealm = localStorage.getItem("kc_realm") || platformRealm;
  const isTenantRealm = currentRealm !== platformRealm;
  const onTenantSubdomain = subdomainDetected;

  const showPlatformSso = !isTenantRealm;
  const showTenantSso = isTenantRealm && tenantConfig?.sso_enabled;
  const showSsoButton = showPlatformSso || showTenantSso;

  const activeIdpHint = isTenantRealm
    ? (tenantConfig?.sso_idp_alias || "oidc")
    : SSO_IDP_HINT;

  const activeSsoLabel = isTenantRealm
    ? (tenantConfig?.sso_label || "Sign in with your organisation")
    : SSO_BUTTON_LABEL;

  const activeSsoHint = isTenantRealm
    ? (tenantConfig?.sso_allowed_domain
      ? `SSO available for @${tenantConfig.sso_allowed_domain} accounts`
      : "Sign in with your organisation account")
    : SSO_HINT_TEXT;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlRealm = params.get("realm");
    if (!onTenantSubdomain && !urlRealm && isTenantRealm) {
      localStorage.removeItem("kc_realm");
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invite email login links use ?invite=1 — hide Forgot Password for first-time setup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromInvite = params.get("invite") === "1";
    let stored = false;
    try {
      stored = sessionStorage.getItem("invite_setup") === "1";
    } catch { /* ignore */ }

    if (fromInvite) {
      try { sessionStorage.setItem("invite_setup", "1"); } catch { /* ignore */ }
      setHideForgotPassword(true);
      // Correct path (invite link) — remove misuse warning / Resend
      clearFirstTimeGate();
      return;
    }
    setHideForgotPassword(stored);
  }, [location.search]);

  useEffect(() => {
    if (!isTenantRealm) {
      setTenantConfig(null);
      return;
    }
    let cancelled = false;
    fetch(`${BACKEND}/api/tenant-config?realm=${encodeURIComponent(currentRealm)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setTenantConfig(data); })
      .catch(() => { if (!cancelled) setTenantConfig(null); });
    return () => { cancelled = true; };
  }, [isTenantRealm, currentRealm]);

  useEffect(() => {
    if (initialized && authenticated && user) {
      navigate(defaultAppPath(user.roles), { replace: true });
    }
  }, [initialized, authenticated, user, navigate]);

  // Show message after forgot-password reset (manual login required)
  useEffect(() => {
    const msg = location.state?.loginMessage;
    if (!msg) return;
    setLoginInfo(msg);
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  const handleSwitchBack = () => {
    localStorage.removeItem("kc_realm");
    window.location.reload();
  };

  const handleTenantLogin = () => {
    const slug = tenantSlug.trim().toLowerCase();
    if (!slug) { setSlugError("Enter your organisation slug"); return; }
    localStorage.setItem("kc_realm", slug);
    window.location.reload();
  };

  // ── SSO: Redirect to Keycloak with kc_idp_hint ──
  const handleSSOLogin = () => {
    const redirectUri = `${window.location.origin}/sso-callback`;
    const state = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem("sso_state", state);
    sessionStorage.setItem("sso_realm", currentRealm);

    const ssoUrl = new URL(`${KC_URL}/realms/${currentRealm}/protocol/openid-connect/auth`);
    ssoUrl.searchParams.append("client_id", KC_CLIENT_ID);
    ssoUrl.searchParams.append("redirect_uri", redirectUri);
    ssoUrl.searchParams.append("response_type", "code");
    ssoUrl.searchParams.append("scope", "openid profile email");
    ssoUrl.searchParams.append("state", state);
    ssoUrl.searchParams.append("kc_idp_hint", activeIdpHint);

    window.location.href = ssoUrl.toString();
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginInfo("");
    // New login attempt re-evaluates misuse; clear previous gate first
    clearFirstTimeGate();
    if (!username.trim()) { setLoginError("Please enter your username."); return; }
    if (!password.trim()) { setLoginError("Please enter your password."); return; }
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim().toLowerCase(),
          password,
          realm: currentRealm,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 403 && data.code === "RESET_REQUIRED") {
          // First-time setup must start from the invite email login link (?invite=1).
          let fromInvite = hideForgotPassword;
          try {
            fromInvite = fromInvite || sessionStorage.getItem("invite_setup") === "1";
          } catch { /* ignore */ }
          if (!fromInvite) {
            try {
              const params = new URLSearchParams(window.location.search);
              fromInvite = params.get("invite") === "1";
            } catch { /* ignore */ }
          }

          if (!fromInvite) {
            setLoginWarning(
              "You need to set up your initial credentials first. Open your invitation email and click the login link to continue."
            );
            return;
          }

          if (data.resetToken) {
            try {
              sessionStorage.setItem("pwd_reset_token", data.resetToken);
            } catch { /* ignore */ }
            navigate(`/change-password?token=${encodeURIComponent(data.resetToken)}`, {
              state: { firstLoginSetup: true },
            });
          } else {
            setLoginError("Password reset is required, but a secure reset link could not be issued. Contact your administrator.");
          }
          return;
        }
        setLoginError(data.message || "Login failed.");
        return;
      }

      try { sessionStorage.removeItem("invite_setup"); } catch { /* ignore */ }
      setHideForgotPassword(false);

      injectTokens({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        id_token: data.id_token,
      });

      const payload = JSON.parse(
        atob(data.access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
      );
      const roles = payload?.realm_access?.roles || [];
      navigate(defaultAppPath(roles), { replace: true });

    } catch {
      setLoginError("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    if (hideForgotPassword) return;
    // Leaving login form for another flow — clear first-time gate
    clearFirstTimeGate();
    setForgotIdentifier(username.trim());
    setForgotResolvedUsername("");
    setForgotError("");
    setForgotSuccess("");
    setForgotLoading(false);
    setAuthPanel("forgotLookup");
  };

  // Login panel (Scenario C): clear first-time gate on success
  const handleResendInvitation = async () => {
    const identifier = username.trim();
    if (!identifier) {
      setLoginError("Enter your username, then try Resend invitation.");
      return;
    }
    setResendLoading(true);
    setLoginError("");
    try {
      const result = await requestResendInvitation(identifier, currentRealm);
      if (!result.ok) {
        setLoginError(result.message || "Could not resend invitation. Please try again later.");
        return;
      }
      clearFirstTimeGate();
      setLoginInfo(result.message || RESEND_INVITE_SUCCESS);
    } catch {
      setLoginError("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  // Forgot-new-user panel (Scenario D): keep warning; show success under it
  const handleForgotResendInvitation = async () => {
    const identifier = (forgotResolvedUsername || forgotIdentifier).trim();
    if (!identifier) {
      setForgotError("Username is required to resend the invitation.");
      return;
    }
    setForgotError("");
    setForgotSuccess("");
    setResendLoading(true);
    try {
      const result = await requestResendInvitation(identifier, currentRealm);
      if (!result.ok) {
        setForgotError(result.message || "Could not resend invitation.");
        return;
      }
      setForgotSuccess(result.message || RESEND_INVITE_SUCCESS);
    } catch {
      setForgotError("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const backToLoginPanel = () => {
    setAuthPanel("login");
    setForgotLoading(false);
    setForgotError("");
    setForgotSuccess("");
    setForgotResolvedUsername("");
  };

  const handleForgotLookup = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotSuccess("");
    const identifier = forgotIdentifier.trim();
    if (!identifier) {
      setForgotError("Please enter your username or email address.");
      return;
    }
    setForgotLoading(true);
    try {
      const statusRes = await fetch(`${BACKEND}/api/check-account-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usernameOrEmail: identifier.toLowerCase(),
          realm: currentRealm,
        }),
      });
      const statusData = await statusRes.json().catch(() => ({}));
      if (!statusRes.ok) {
        setForgotError(statusData.message || "Unable to check account status.");
        return;
      }

      if (statusData.isNewUser) {
        setForgotResolvedUsername(statusData.username || identifier.toLowerCase());
        setAuthPanel("forgotNewUser");
        return;
      }

      const res = await fetch(`${BACKEND}/api/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          usernameOrEmail: identifier.toLowerCase(),
          realm: currentRealm,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setForgotError(data.message || "Unable to process password reset request.");
        return;
      }
      setForgotSuccess(
        data.message ||
        "If an account exists for this username/email, a password reset link has been sent to your email address."
      );
      setAuthPanel("forgotExisting");
    } catch {
      setForgotError("Network error. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    // ✅ FIGMA: 1440×1024 full split layout
    <Box sx={{
      display: "flex",
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      bgcolor: "#FFFFFF",
    }}>

      {/* ── LEFT PANEL — Figma: 480px, gradient #001233→#0046B8 ── */}
      <Box sx={{
        width: { xs: "0px", md: "480px" },
        minWidth: { xs: "0px", md: "480px" },
        height: "100%",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        background: "linear-gradient(180deg, #001233 0%, #002266 35%, #003399 70%, #0046B8 100%)",
        padding: "40px 48px",
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Glow decoration */}
        <Box sx={{
          position: "absolute", width: "600px", height: "600px", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,70,184,0.5) 0%, transparent 65%)",
          top: "-200px", right: "-200px", pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", width: "500px", height: "500px", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,51,153,0.4) 0%, transparent 65%)",
          bottom: "-100px", left: "-150px", pointerEvents: "none",
        }} />

        {/* Logo — top left */}
        <Box component="img" src={logo}
          sx={{ height: 44, objectFit: "contain", objectPosition: "left", filter: "brightness(0) invert(1)", zIndex: 1 }}
        />

        {/* Center text */}
        <Box sx={{ zIndex: 1 }}>
          <Typography sx={{
            fontSize: { md: "28px", lg: "36px" },
            fontWeight: 800, color: "#FFFFFF", lineHeight: 1.25, mb: 2,
          }}>
            Govern AI.<br />
            Ensure Compliance.<br />
            <Box component="span" sx={{ color: "#38BDF8" }}>Build Trust.</Box>
          </Typography>
          <Typography sx={{
            fontSize: "13px", color: "rgba(255,255,255,0.55)",
            lineHeight: 1.7, maxWidth: "320px",
          }}>
            The enterprise-grade platform for validating, monitoring,
            and governing AI systems across your organization.
          </Typography>
        </Box>

        {/* Footer */}
        <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", zIndex: 1 }}>
          © QualiZeal 2026. All Rights Reserved
        </Typography>
      </Box>

      {/* ── RIGHT PANEL — Figma: white, flex center ── */}
      <Box sx={{
        flex: 1,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#FFFFFF",
        overflowY: "auto",
        padding: { xs: "24px", md: "40px" },
      }}>

        {/* ── CARD — Figma: 640×504, centered ── */}
        <Box sx={{
          width: "100%",
          maxWidth: "640px",
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0px 8px 32px rgba(0,0,0,0.08)",
          padding: { xs: "32px 24px", md: "48px 64px" },
        }}>

          {/* Icon + Title — Figma: centered */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box component="img" src={icon} sx={{ width: 52, height: 52, mb: 1.5 }} />
            <Typography sx={{ fontSize: "26px", fontWeight: 700, color: "#111827" }}>
              Get Started
            </Typography>
          </Box>

          {/* Tenant realm badge */}
          {isTenantRealm && (
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              bgcolor: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: "8px",
              px: 1.5, py: 1, mb: 2,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BusinessIcon sx={{ fontSize: 16, color: "#0EA5E9" }} />
                <Typography fontSize="13px" color="#0369A1" fontWeight={500}>
                  Realm: <strong>{currentRealm}</strong>
                </Typography>
              </Box>
              <Link component="button" onClick={handleSwitchBack}
                sx={{ fontSize: "12px", color: "#9CA3AF", cursor: "pointer" }}>
                Switch back
              </Link>
            </Box>
          )}

          {/* ── FORM / FORGOT PANELS (same card slot — no dialog) ── */}
          {authPanel === "login" && (
          <>
          {/* noValidate disables the browser's native HTML5 validation bubble
              ("Please fill out this field."), letting our own red Alert show
              the friendlier "Please enter your password." message instead. */}
          <form onSubmit={handleSignIn} noValidate>
            {loginInfo && (
              <Alert severity="success" sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}>
                {loginInfo}
              </Alert>
            )}
            {loginError && (
              <Alert severity="error" sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}>
                {loginError}
              </Alert>
            )}

            {/* Template A: first-time misuse warning — directly above username */}
            {loginWarning && (
              <Alert
                severity="warning"
                onClose={clearFirstTimeGate}
                sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}
              >
                {loginWarning}
              </Alert>
            )}

            {/* Username — Figma: label "*Username" above field */}
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", mb: 0.5 }}>
              <Box component="span" sx={{ color: "#E53935", mr: 0.25 }} aria-hidden="true">*</Box>
              Username
            </Typography>
            <TextField fullWidth size="small"
              required
              aria-required="true"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setLoginError("");
              }}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /></InputAdornment>,
                sx: { borderRadius: "6px", fontSize: "14px", bgcolor: "#FAFAFA" },
              }}
            />

            {/* Password */}
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", mb: 0.5 }}>
              <Box component="span" sx={{ color: "#E53935", mr: 0.25 }} aria-hidden="true">*</Box>
              Password
            </Typography>
            <TextField fullWidth size="small"
              required
              aria-required="true"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setLoginError("");
              }}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: "6px", fontSize: "14px", bgcolor: "#FAFAFA" },
              }}
            />

            {/* Remember me + Forgot Password — Figma: same row (hide Forgot on invite setup link) */}
            <Box sx={{
              display: "flex",
              justifyContent: hideForgotPassword ? "flex-start" : "space-between",
              alignItems: "center",
              mb: 3,
            }}>
              <FormControlLabel
                control={
                  <Checkbox size="small" checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{ color: "#D1D5DB", "&.Mui-checked": { color: "#1D4ED8" } }}
                  />
                }
                label={<Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Remember me</Typography>}
              />
              {!hideForgotPassword && (
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={openForgotPassword}
                  sx={{ fontSize: "13px", color: "#E53935", fontWeight: 500, cursor: "pointer", border: 0, background: "none", p: 0 }}
                >
                  Forgot Password?
                </Link>
              )}
            </Box>

            {/* Login Button */}
            <Button type="submit" variant="contained" fullWidth disabled={loading}
              sx={{
                bgcolor: "#1D4ED8", borderRadius: "6px", textTransform: "none",
                py: 1.4, fontSize: "15px", fontWeight: 600, boxShadow: "none",
                mb: loginWarning ? 1.5 : 2,
                "&:hover": { bgcolor: "#1E40AF" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Login"}
            </Button>

            {/* Template A: Resend invitation — only while first-time misuse warning is shown */}
            {loginWarning && (
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                  Didn&apos;t get the email?{" "}
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    disabled={resendLoading}
                    onClick={handleResendInvitation}
                    sx={{
                      fontSize: "13px",
                      color: "#1D4ED8",
                      fontWeight: 600,
                      cursor: resendLoading ? "default" : "pointer",
                      border: 0,
                      background: "none",
                      p: 0,
                      verticalAlign: "baseline",
                    }}
                  >
                    {resendLoading ? "Sending…" : "Resend invitation"}
                  </Link>
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#9CA3AF", mt: 0.5 }}>
                  Still stuck? Contact your admin
                </Typography>
              </Box>
            )}

            {/* OR divider + SSO — platform env SSO or tenant OIDC when configured */}
            {showSsoButton && (
              <>
                <Divider sx={{ my: 2, fontSize: "12px", color: "#9CA3AF" }}>or</Divider>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleSSOLogin}
                  sx={{
                    borderColor: "#D1D5DB", color: "#374151", borderRadius: "6px",
                    textTransform: "none", py: 1.3, fontSize: "14px", fontWeight: 500,
                    mb: 1, bgcolor: "#FFFFFF",
                    "&:hover": { borderColor: "#0078D4", bgcolor: "#F5FAFF" },
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
                  }}
                >
                  {(activeIdpHint === "microsoft" || SSO_IDP_HINT === "microsoft") && (
                    <Box component="span" sx={{ display: "inline-flex", width: 18, height: 18 }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21">
                        <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                        <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                        <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                        <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                      </svg>
                    </Box>
                  )}
                  {activeSsoLabel}
                </Button>
                <Typography sx={{ textAlign: "center", fontSize: "11px", color: "#9CA3AF", mb: 1 }}>
                  {activeSsoHint}
                </Typography>
              </>
            )}
          </form>

          {/* Tenant section */}
          {!onTenantSubdomain && (
            <>
              <Divider sx={{ my: 3, fontSize: "12px", color: "#9CA3AF" }}>
                or sign in as a tenant
              </Divider>
              <TextField fullWidth size="small"
                placeholder="Enter your org slug (e.g. google)"
                value={tenantSlug}
                onChange={(e) => { setTenantSlug(e.target.value); setSlugError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleTenantLogin()}
                error={!!slugError} helperText={slugError}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /></InputAdornment>,
                  sx: { borderRadius: "6px", fontSize: "14px", bgcolor: "#FAFAFA" },
                }}
                sx={{ mb: 1.5 }}
              />
              <Button variant="outlined" fullWidth
                endIcon={<ArrowForwardIcon />} onClick={handleTenantLogin}
                sx={{
                  borderColor: "#0EA5E9", color: "#0EA5E9", borderRadius: "6px",
                  textTransform: "none", py: 1.25, fontSize: "14px", fontWeight: 500,
                  "&:hover": { borderColor: "#0284C7", color: "#0284C7", bgcolor: "#F0F9FF" },
                }}
              >
                Sign in as Tenant
              </Button>
            </>
          )}
          </>
          )}

          {authPanel === "forgotLookup" && (
            <form onSubmit={handleForgotLookup} noValidate>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827", mb: 1 }}>
                Forgot Password
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#6B7280", mb: 2 }}>
                Enter your username or email address. If an account exists and is eligible,
                we will send a password reset link.
              </Typography>
              {forgotError && (
                <Alert severity="error" sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}>
                  {forgotError}
                </Alert>
              )}
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", mb: 0.5 }}>
                Username or Email
              </Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                placeholder="Enter username or email"
                value={forgotIdentifier}
                onChange={(e) => { setForgotIdentifier(e.target.value); setForgotError(""); }}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "6px", fontSize: "14px", bgcolor: "#FAFAFA" },
                }}
              />
              <Button type="submit" variant="contained" fullWidth disabled={forgotLoading}
                sx={{
                  bgcolor: "#1D4ED8", borderRadius: "6px", textTransform: "none",
                  py: 1.4, fontSize: "15px", fontWeight: 600, boxShadow: "none", mb: 2,
                  "&:hover": { bgcolor: "#1E40AF" },
                }}
              >
                {forgotLoading ? <CircularProgress size={20} color="inherit" /> : "Continue"}
              </Button>
              <Typography sx={{ textAlign: "center", fontSize: "13px", color: "#9CA3AF" }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={backToLoginPanel}
                  sx={{ color: "#1D4ED8", fontWeight: 600, fontSize: "13px", border: 0, background: "none", cursor: "pointer" }}
                >
                  Back to Login
                </Link>
              </Typography>
            </form>
          )}

          {authPanel === "forgotExisting" && (
            <Box>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827", mb: 1 }}>
                Forgot Password
              </Typography>
              <Alert severity="success" sx={{ mb: 3, fontSize: "13px", borderRadius: "8px" }}>
                {forgotSuccess ||
                  "If an account exists for this username/email, a password reset link has been sent to your email address."}
              </Alert>
              <Button variant="contained" fullWidth onClick={backToLoginPanel}
                sx={{
                  bgcolor: "#1D4ED8", borderRadius: "6px", textTransform: "none",
                  py: 1.4, fontSize: "15px", fontWeight: 600, boxShadow: "none",
                  "&:hover": { bgcolor: "#1E40AF" },
                }}
              >
                Back to Login
              </Button>
            </Box>
          )}

          {authPanel === "forgotNewUser" && (
            <Box>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827", mb: 1 }}>
                First-Time Password Setup
              </Typography>
              <Alert severity="warning" sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}>
                Forgot Password is for accounts that already have a permanent password.
                Finish setup via your invitation email — open the login link in that email to continue.
              </Alert>
              {forgotSuccess && (
                <Alert severity="success" sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}>
                  {forgotSuccess}
                </Alert>
              )}
              {forgotError && (
                <Alert severity="error" sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}>
                  {forgotError}
                </Alert>
              )}
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", mb: 0.5 }}>
                Username or Email
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={forgotResolvedUsername || forgotIdentifier}
                disabled
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "6px",
                    fontSize: "14px",
                    bgcolor: "#F3F4F6",
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#111827",
                      color: "#111827",
                    },
                  },
                }}
                sx={{ mb: 3 }}
              />
              <Button
                variant="contained"
                fullWidth
                disabled={resendLoading}
                onClick={handleForgotResendInvitation}
                sx={{
                  bgcolor: "#1D4ED8", borderRadius: "6px", textTransform: "none",
                  py: 1.4, fontSize: "15px", fontWeight: 600, boxShadow: "none", mb: 2,
                  "&:hover": { bgcolor: "#1E40AF" },
                }}
              >
                {resendLoading ? <CircularProgress size={20} color="inherit" /> : "Resend invitation"}
              </Button>
              <Typography sx={{ textAlign: "center", fontSize: "13px", color: "#9CA3AF", mb: 1 }}>
                Still stuck? Contact your admin
              </Typography>
              <Typography sx={{ textAlign: "center", fontSize: "13px", color: "#9CA3AF" }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => {
                    setForgotError("");
                    setForgotSuccess("");
                    setAuthPanel("forgotLookup");
                  }}
                  sx={{ color: "#1D4ED8", fontWeight: 600, fontSize: "13px", border: 0, background: "none", cursor: "pointer", mr: 2 }}
                >
                  Back
                </Link>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={backToLoginPanel}
                  sx={{ color: "#6B7280", fontWeight: 500, fontSize: "13px", border: 0, background: "none", cursor: "pointer" }}
                >
                  Back to Login
                </Link>
              </Typography>
            </Box>
          )}

          {/* Footer */}
          <Typography sx={{ textAlign: "center", fontSize: "11px", color: "#9CA3AF", mt: 3 }}>
            By signing in, you agree to our{" "}
            <Link href="#" underline="hover" sx={{ color: "#0EA5E9" }}>Terms of Use</Link>
            {" "}&{" "}
            <Link href="#" underline="hover" sx={{ color: "#0EA5E9" }}>Privacy Policy</Link>
          </Typography>

        </Box>
      </Box>
    </Box>
  );
}

export default Login;
